import React from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRightIcon, MapPinIcon } from 'lucide-react';
import { SmartLink } from '../components/SmartLink';
import { StoryFactsCard } from '../components/stories/StoryFactsCard';
import { StoryCard } from '../components/stories/StoryCard';
import { ProductVisualView } from '../components/product/ProductVisualView';
import { PlaceholderNote } from '../components/company/PlaceholderNote';
import { CtaBand } from '../components/company/CtaBand';
import { customerStories } from '../data/stories';
import { solutionCatalog } from '../data/solutions/catalog';
import { NotFound } from './NotFound';
export function CustomerStory() {
  const {
    slug = ''
  } = useParams();
  const story = customerStories.find(s => s.slug === slug);
  if (!story) return <NotFound data-id="element-1983" data-mp="16sz0ks7zhi8f" />;
  const industry = solutionCatalog[story.industry];
  const related = customerStories.filter(s => s.slug !== story.slug).map(s => ({
    s,
    score: (s.industry === story.industry ? 2 : 0) + s.products.filter(p => story.products.includes(p)).length
  })).sort((a, b) => b.score - a.score).slice(0, 3).map(({
    s
  }) => s);
  return <main data-id="element-1984" data-mp="26xro31useejz">
      <section className="hero-surface w-full" aria-labelledby="story-heading" data-id="element-1985" data-mp="26xhz9hlhadfv">
        <div className="mx-auto max-w-shell px-6 pb-12 pt-6" data-id="element-1986" data-mp="2271j3pq9wdpt">
          <nav aria-label="Breadcrumb" data-id="element-1987" data-mp="225i3m1cfy6p3">
            <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-ink-muted" data-id="element-1988" data-mp="2gvxmvalar4km">
              <li data-id="element-1989" data-mp="2bwuuapmi6qtj">
                <SmartLink href="/customers" className="transition-colors duration-150 ease-out hover:text-ink" data-id="element-1990" data-mp="2bxdzr6uyvmqo">
                  Customers
                </SmartLink>
              </li>
              <li aria-hidden="true" data-id="element-1991" data-mp="2kxsnsdzbffxv">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1992" data-mp="2kyuc3k5cdyxj" />
              </li>
              <li aria-current="page" className="font-medium text-ink" data-id="element-1993" data-mp="vi32oex6lnla">
                {story.business}
              </li>
            </ol>
          </nav>

          <div className="mt-10 max-w-[860px] lg:mt-14" data-id="element-1994" data-mp="qlmexltw9b8q">
            <div className="flex flex-wrap items-center gap-2" data-id="element-1995" data-mp="2qmswgx3uz39n">
              {industry && <SmartLink href={industry.href} className="rounded-full bg-white px-3 py-1 text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:text-brand" data-id="element-1996" data-mp="2vlx6fpylx511">
                  {industry.title}
                </SmartLink>}
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[13px] font-medium text-ink" data-id="element-1997" data-mp="39ju4yrwmflic">
                <MapPinIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-1998" data-mp="3ehncbo9nulk7" />
                {story.location}
              </span>
            </div>
            <h1 id="story-heading" className="display mt-5 text-[36px] font-semibold text-ink sm:text-[48px] lg:text-[56px]" data-id="element-1999" data-mp="3o8hv2inp730w">
              {story.headline}
            </h1>
          </div>
          <img src={story.image} alt={story.alt} className="mt-10 h-[300px] w-full rounded-[16px] object-cover sm:h-[440px] lg:h-[560px]" data-id="element-2000" data-mp="1422qc2y7hxd" />
        </div>
      </section>

      <div className="mx-auto grid max-w-shell grid-cols-1 gap-10 px-6 py-12 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-16 lg:py-20" data-id="element-2001" data-mp="1gmqa7zo9jzsd">
        <div className="lg:order-2" data-id="element-2002" data-mp="1coadh42drd1r">
          <div className="lg:sticky lg:top-[104px]" data-id="element-2003" data-mp="17nmmgie5iw7j">
            <StoryFactsCard story={story} data-id="element-2004" data-mp="17oxtcr2w8k2v" />
          </div>
        </div>

        <article className="min-w-0 lg:order-1" data-id="element-2005" data-mp="24c2dyk0tnfna">
          <section aria-labelledby="key-results-heading" data-id="element-2006" data-mp="20bqu39ga9jcv">
            <h2 id="key-results-heading" className="sr-only" data-id="element-2007" data-mp="20caeqxxbilul">
              Key results
            </h2>
            <dl className="grid grid-cols-1 gap-6 sm:grid-cols-3" data-id="element-2008" data-mp="1vjh5bwqwmpc6">
              {story.results.map(r => <div key={r.label} className="border-t-2 border-brand pt-4" data-id="element-2009" data-mp="1qjuwdzi3zv96">
                  <dt className="sr-only" data-id="element-2010" data-mp="2o2w95meeg7m4">{r.label}</dt>
                  <dd data-id="element-2011" data-mp="2jawcc2tmlutr">
                    <span className="display block text-[44px] font-semibold text-ink" data-id="element-2012" data-mp="2j9lbobvh936v">{r.value}</span>
                    <span className="mt-1 block text-[15px] text-ink-muted" data-id="element-2013" data-mp="22u2c0rl6dvhf">{r.label}</span>
                  </dd>
                </div>)}
            </dl>
            <PlaceholderNote className="mt-6" data-id="element-2014" data-mp="1tup3q28sfcya">Results are placeholders until confirmed by the customer.</PlaceholderNote>
          </section>

          <div className="mt-14 max-w-[680px] space-y-12" data-id="element-2015" data-mp="1yubgybom8t73">
            <StorySection id="challenge" title="The challenge" paragraphs={story.challenge} data-id="element-2016" data-mp="2mjcq6159w8df" />
            <StorySection id="solution" title="The solution" paragraphs={story.solution} data-id="element-2017" data-mp="2qkg9wyqfyojx" />
          </div>

          <figure className="mt-10" data-id="element-2018" data-mp="2ctta138y2sm9">
            <img src={story.inlineImage.src} alt={story.inlineImage.alt} loading="lazy" className="h-[280px] w-full rounded-[14px] object-cover sm:h-[400px]" data-id="element-2019" data-mp="2csrxsvg7002y" />
            <figcaption className="mt-3 text-[14px] text-ink-muted" data-id="element-2020" data-mp="35fvyio895s8x">{story.inlineImage.caption}</figcaption>
          </figure>

          <figure className="my-14 border-l-4 border-brand py-2 pl-6 sm:pl-8" data-id="element-2021" data-mp="3jn8yxduu1fjb">
            <blockquote className="display text-[28px] font-semibold text-ink sm:text-[36px]" data-id="element-2022" data-mp="3jl4mu8ms5a7y">“{story.quote}”</blockquote>
            <figcaption className="mt-5 text-[15px] text-ink-muted" data-id="element-2023" data-mp="3tdtsunzk9vvw">
              — <span className="font-semibold text-ink" data-id="element-2024" data-mp="3tf4v2jdcyvkw">{story.owner}</span>, {story.role}, {story.business}
            </figcaption>
          </figure>

          <div className="max-w-[680px]" data-id="element-2025" data-mp="30po5rul1busw">
            <StorySection id="results" title="The results" paragraphs={story.outcome} data-id="element-2026" data-mp="30qpmaektl9i0" />
          </div>

          <figure className="mt-12" data-id="element-2027" data-mp="2wpe4gv86tskd">
            <div className="flex min-h-[320px] items-center justify-center rounded-[16px] bg-canvas-lilac px-6 py-10 sm:px-10" data-id="element-2028" data-mp="3an2nrymlwrnb">
              <ProductVisualView visual={story.screenshot.visual} className="flex w-full justify-center" data-id="element-2029" data-mp="35m4w8vfpil95" />
            </div>
            <figcaption className="mt-3 text-[14px] text-ink-muted" data-id="element-2030" data-mp="pyoarfvsg3j3">{story.screenshot.caption}</figcaption>
          </figure>
        </article>
      </div>

      <section className="w-full bg-canvas-warm py-20" aria-labelledby="related-stories-heading" data-id="element-2031" data-mp="sx516qvetpbt">
        <div className="mx-auto max-w-shell px-6" data-id="element-2032" data-mp="wyql2w6d33vi">
          <h2 id="related-stories-heading" className="display text-[30px] font-semibold text-ink sm:text-[40px]" data-id="element-2033" data-mp="x1mjsmq19wl0">
            More stories like this
          </h2>
          <ul className="mt-10 grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-3" data-id="element-2034" data-mp="16xfrnsst51wg">
            {related.map(s => <li key={s.slug} data-id="element-2035" data-mp="984cn3sg3hkz">
                <StoryCard story={s} data-id="element-2036" data-mp="e90qa92lmi3l" />
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand headline="Ready to write your own success story?" subheadline="Set up in minutes. Local support in Kigali when you need it." primary={{
      label: 'Get started',
      href: '/get-started'
    }} secondary={{
      label: 'Book a demo',
      href: '/demo'
    }} data-id="element-2037" data-mp="j83ll1ss19yd" />
    </main>;
}
function StorySection({
  id,
  title,
  paragraphs
}: {
  id: string;
  title: string;
  paragraphs: string[];
}) {
  return <section aria-labelledby={`${id}-heading`} data-id="element-2038" data-mp="3k3cq3qww7l0u">
      <h2 id={`${id}-heading`} className="display text-[26px] font-semibold text-ink sm:text-[30px]" data-id="element-2039" data-mp="3f5kxsvsc5ww4">
        {title}
      </h2>
      <div className="mt-4 space-y-4" data-id="element-2040" data-mp="3a5oukyrgq9hm">
        {paragraphs.map(p => <p key={p} className="text-[17px] leading-[1.7] text-ink-soft" data-id="element-2041" data-mp="357euqausmkpo">
            {p}
          </p>)}
      </div>
    </section>;
}